import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import {
  GlobalCommerceApiService,
  GlobalCommerceCurrencyRow,
  GlobalCommerceFxRateRow,
  GlobalCommerceJurisdictionPackRow,
  GlobalCommerceReadinessSummary,
  GlobalCommerceShippingZoneRow,
  GlobalCommerceTaxCategoryRow,
  GlobalCommerceTaxJurisdictionRow,
  GlobalCommerceTaxRateRow,
  GlobalCommerceTaxRuleRow,
  EInvoiceRequirementResult
} from './global-commerce-api.service';

@Component({
  standalone: true,
  selector: 'ain-global-commerce-admin',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    TabsModule,
    TagModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    RouterLink,
    DatePipe
  ],
  template: `
    <ain-page
      [title]="t('catalog.globalCommerce.title')"
      [description]="t('catalog.globalCommerce.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.globalCommerce.help.title'"
        [descriptionKey]="'catalog.globalCommerce.help.description'"
        [bulletKeys]="[
          'catalog.globalCommerce.help.bullets.foundation',
          'catalog.globalCommerce.help.bullets.checkout',
          'catalog.globalCommerce.help.bullets.deferrals'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('catalog.globalCommerce.unavailable')" [message]="error() ?? ''" />
      } @else {
        @if (readiness()) {
          <section class="panel summary-cards mb-3">
            <div class="card">
              <span class="label">{{ t('catalog.globalCommerce.readiness.decision') }}</span>
              <p-tag [severity]="decisionSeverity(readiness()!.launchDecision)" [value]="readiness()!.launchDecision" />
            </div>
            <div class="card">
              <span class="label">{{ t('catalog.globalCommerce.readiness.ready') }}</span>
              <strong>{{ readiness()!.readyCount }}</strong>
            </div>
            <div class="card">
              <span class="label">{{ t('catalog.globalCommerce.readiness.blockers') }}</span>
              <strong>{{ readiness()!.blockerCount }}</strong>
            </div>
          </section>
        }

        <section class="toolbar mb-3">
          <button pButton type="button" [label]="t('common.refresh')" (click)="reload()"></button>
          <a pButton type="button" class="p-button-outlined" routerLink="/marketplace/launch-readiness"
             [label]="t('catalog.globalCommerce.linkLaunchReadiness')"></a>
        </section>

        <p-tabs value="readiness">
          <p-tablist>
            <p-tab value="readiness">{{ t('catalog.globalCommerce.tabs.readiness') }}</p-tab>
            <p-tab value="currency">{{ t('catalog.globalCommerce.tabs.currencyFx') }}</p-tab>
            <p-tab value="tax">{{ t('catalog.globalCommerce.tabs.tax') }}</p-tab>
            <p-tab value="jurisdiction">{{ t('catalog.globalCommerce.tabs.jurisdiction') }}</p-tab>
            <p-tab value="shipping">{{ t('catalog.globalCommerce.tabs.shipping') }}</p-tab>
            <p-tab value="einvoice">{{ t('catalog.globalCommerce.tabs.einvoice') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="readiness">
              @if (readiness()) {
                <p-table [value]="readiness()!.checks">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.globalCommerce.columns.domain') }}</th>
                      <th>{{ t('catalog.globalCommerce.columns.count') }}</th>
                      <th>{{ t('catalog.globalCommerce.columns.status') }}</th>
                      <th>{{ t('catalog.globalCommerce.columns.notes') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.label }} ({{ row.key }})</td>
                      <td>{{ row.count }}</td>
                      <td><p-tag [severity]="statusSeverity(row.status)" [value]="row.status" /></td>
                      <td>{{ row.notes }}</td>
                    </tr>
                  </ng-template>
                </p-table>
                <p class="mt-3 text-muted">{{ t('catalog.globalCommerce.deferralsTitle') }}: {{ readiness()!.deferrals.join(', ') }}</p>
              }
            </p-tabpanel>

            <p-tabpanel value="currency">
              <h3 class="h6">{{ t('catalog.globalCommerce.fxRates') }}</h3>
              <div class="row g-2 mb-3">
                <div class="col-md-2"><input pInputText [(ngModel)]="fxForm.fromCurrencyCode" placeholder="From" /></div>
                <div class="col-md-2"><input pInputText [(ngModel)]="fxForm.toCurrencyCode" placeholder="To" /></div>
                <div class="col-md-2"><input pInputText type="date" [(ngModel)]="fxForm.effectiveDateUtc" /></div>
                <div class="col-md-2"><input pInputText type="number" [(ngModel)]="fxForm.rate" placeholder="Rate" /></div>
                <div class="col-md-2"><button pButton type="button" [label]="t('common.create')" (click)="saveFxRate()"></button></div>
              </div>
              <p-table [value]="fxRates()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>Date</th><th>From</th><th>To</th><th>Rate</th><th>Status</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.effectiveDateUtc | date:'yyyy-MM-dd' }}</td>
                    <td>{{ row.fromCurrencyCode }}</td>
                    <td>{{ row.toCurrencyCode }}</td>
                    <td>{{ row.rate }}</td>
                    <td><p-tag [value]="row.approvalStatus" /></td>
                  </tr>
                </ng-template>
              </p-table>
              <h3 class="h6 mt-4">{{ t('catalog.globalCommerce.currencies') }}</h3>
              <p-table [value]="currencies()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Code</th><th>Name</th><th>Symbol</th><th>Active</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.code }}</td><td>{{ row.name }}</td><td>{{ row.symbol }}</td>
                    <td><p-tag [severity]="row.isActive ? 'success' : 'secondary'" [value]="row.isActive ? 'active' : 'inactive'" /></td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="tax">
              <div class="row g-2 mb-3">
                <div class="col-md-2"><input pInputText [(ngModel)]="taxJurisdictionForm.jurisdictionCode" [placeholder]="t('catalog.globalCommerce.fields.jurisdictionCode')" /></div>
                <div class="col-md-2"><input pInputText [(ngModel)]="taxJurisdictionForm.countryCode" placeholder="Country" /></div>
                <div class="col-md-2"><input pInputText [(ngModel)]="taxJurisdictionForm.currencyCode" placeholder="Currency" /></div>
                <div class="col-md-2"><p-checkbox [(ngModel)]="taxJurisdictionForm.isActive" [binary]="true" label="Active" /></div>
                <div class="col-md-2"><button pButton type="button" [label]="t('common.create')" (click)="saveTaxJurisdiction()"></button></div>
              </div>
              <p-table [value]="taxJurisdictions()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Code</th><th>Country</th><th>Currency</th><th>Status</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.jurisdictionCode }}</td><td>{{ row.countryCode }}</td><td>{{ row.currencyCode }}</td>
                    <td><p-tag [severity]="row.isActive ? 'success' : 'secondary'" [value]="row.isActive ? 'active' : 'inactive'" /></td>
                  </tr>
                </ng-template>
              </p-table>

              <h3 class="h6 mt-4">{{ t('catalog.globalCommerce.taxCategories') }}</h3>
              <div class="row g-2 mb-3">
                <div class="col-md-2"><input pInputText [(ngModel)]="taxCategoryForm.categoryCode" placeholder="Code" /></div>
                <div class="col-md-3"><input pInputText [(ngModel)]="taxCategoryForm.name" placeholder="Name" /></div>
                <div class="col-md-2"><button pButton type="button" [label]="t('common.create')" (click)="saveTaxCategory()"></button></div>
              </div>
              <p-table [value]="taxCategories()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Code</th><th>Name</th><th>Exempt</th><th>Zero</th><th>Active</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.categoryCode }}</td><td>{{ row.name }}</td>
                    <td>{{ row.isExempt }}</td><td>{{ row.isZeroRated }}</td>
                    <td>{{ row.isActive }}</td>
                  </tr>
                </ng-template>
              </p-table>

              <h3 class="h6 mt-4">{{ t('catalog.globalCommerce.taxRates') }}</h3>
              <p-table [value]="taxRates()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Jurisdiction</th><th>Category</th><th>Rate %</th><th>Active</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.taxJurisdictionId }}</td><td>{{ row.taxCategoryId }}</td>
                    <td>{{ row.ratePercent }}</td><td>{{ row.isActive }}</td>
                  </tr>
                </ng-template>
              </p-table>

              <h3 class="h6 mt-4">{{ t('catalog.globalCommerce.taxRules') }}</h3>
              <p-table [value]="taxRules()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Jurisdiction</th><th>Category</th><th>Mode</th><th>Active</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.taxJurisdictionId }}</td><td>{{ row.taxCategoryId }}</td>
                    <td>{{ row.pricingMode }}</td><td>{{ row.isActive }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="jurisdiction">
              <div class="row g-2 mb-3">
                <div class="col-md-2"><input pInputText [(ngModel)]="jurisdictionPackForm.jurisdictionCode" placeholder="Code" /></div>
                <div class="col-md-2"><input pInputText [(ngModel)]="jurisdictionPackForm.countryCode" placeholder="Country" /></div>
                <div class="col-md-2"><p-checkbox [(ngModel)]="jurisdictionPackForm.isEnabled" [binary]="true" label="Enabled" /></div>
                <div class="col-md-2"><p-checkbox [(ngModel)]="jurisdictionPackForm.allowCrossBorder" [binary]="true" label="Cross-border" /></div>
                <div class="col-md-2"><button pButton type="button" [label]="t('common.create')" (click)="saveJurisdictionPack()"></button></div>
              </div>
              <p-table [value]="jurisdictionPacks()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Code</th><th>Country</th><th>Enabled</th><th>Cross-border</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.jurisdictionCode }}</td><td>{{ row.countryCode }}</td>
                    <td><p-tag [severity]="row.isEnabled ? 'success' : 'secondary'" [value]="row.isEnabled ? 'enabled' : 'disabled'" /></td>
                    <td>{{ row.allowCrossBorder }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="shipping">
              <div class="row g-2 mb-3">
                <div class="col-md-2"><input pInputText [(ngModel)]="shippingZoneForm.zoneCode" placeholder="Zone" /></div>
                <div class="col-md-3"><input pInputText [(ngModel)]="shippingZoneForm.name" placeholder="Name" /></div>
                <div class="col-md-2"><input pInputText [(ngModel)]="shippingZoneForm.countryCode" placeholder="Country" /></div>
                <div class="col-md-2"><button pButton type="button" [label]="t('common.create')" (click)="saveShippingZone()"></button></div>
              </div>
              <p-table [value]="shippingZones()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr><th>Zone</th><th>Name</th><th>Country</th><th>Active</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.zoneCode }}</td><td>{{ row.name }}</td><td>{{ row.countryCode }}</td>
                    <td>{{ row.isActive }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="einvoice">
              <div class="row g-2 mb-3 align-items-end">
                <div class="col-md-3">
                  <label class="form-label">{{ t('catalog.globalCommerce.fields.jurisdictionCode') }}</label>
                  <input pInputText class="w-100" [(ngModel)]="einvoiceJurisdictionCode" />
                </div>
                <div class="col-md-2">
                  <button pButton type="button" [label]="t('catalog.globalCommerce.checkEinvoice')" (click)="checkEinvoice()"></button>
                </div>
              </div>
              @if (einvoiceResult()) {
                <p-tag [severity]="einvoiceResult()!.isRequired ? 'warn' : 'success'"
                  [value]="einvoiceResult()!.isRequired ? 'required' : 'not_required'" />
                <p class="mt-2">Provider: {{ einvoiceResult()!.providerKey }} / Format: {{ einvoiceResult()!.formatCode }}</p>
                @if (einvoiceResult()!.requiredFields.length) {
                  <p>Required fields: {{ einvoiceResult()!.requiredFields.join(', ') }}</p>
                }
              }
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </ain-page>
  `,
  styles: [`
    .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
    .summary-cards .card { border: 1px solid var(--surface-border); border-radius: 8px; padding: 1rem; }
    .summary-cards .label { display: block; font-size: 0.85rem; color: var(--text-color-secondary); margin-bottom: 0.25rem; }
    .toolbar { display: flex; gap: 0.75rem; flex-wrap: wrap; }
  `]
})
export class GlobalCommerceAdminComponent implements OnInit {
  private readonly api = inject(GlobalCommerceApiService);
  private readonly localization = inject(LocalizationService);

  loading = signal(true);
  error = signal<string | null>(null);
  readiness = signal<GlobalCommerceReadinessSummary | null>(null);
  currencies = signal<GlobalCommerceCurrencyRow[]>([]);
  fxRates = signal<GlobalCommerceFxRateRow[]>([]);
  taxJurisdictions = signal<GlobalCommerceTaxJurisdictionRow[]>([]);
  taxCategories = signal<GlobalCommerceTaxCategoryRow[]>([]);
  taxRates = signal<GlobalCommerceTaxRateRow[]>([]);
  taxRules = signal<GlobalCommerceTaxRuleRow[]>([]);
  jurisdictionPacks = signal<GlobalCommerceJurisdictionPackRow[]>([]);
  shippingZones = signal<GlobalCommerceShippingZoneRow[]>([]);
  einvoiceResult = signal<EInvoiceRequirementResult | null>(null);
  einvoiceJurisdictionCode = 'IR';

  fxForm = { fromCurrencyCode: 'EUR', toCurrencyCode: 'USD', effectiveDateUtc: new Date().toISOString().slice(0, 10), rate: 1 };
  taxJurisdictionForm = { jurisdictionCode: '', countryCode: 'IR', currencyCode: 'IRR', isActive: true };
  taxCategoryForm = { categoryCode: '', name: '', isExempt: false, isZeroRated: false, isActive: true };
  jurisdictionPackForm = { jurisdictionCode: '', countryCode: 'IR', isEnabled: true, allowCrossBorder: true };
  shippingZoneForm = { zoneCode: '', name: '', countryCode: 'IR', isActive: true };

  ngOnInit(): void {
    this.reload();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getReadinessSummary().subscribe({
      next: summary => {
        this.readiness.set(summary);
        this.api.listCurrencies().subscribe({ next: rows => this.currencies.set(rows), error: () => {} });
        this.api.listFxRates().subscribe({ next: rows => this.fxRates.set(rows), error: () => {} });
        this.api.listTaxJurisdictions().subscribe({ next: rows => this.taxJurisdictions.set(rows), error: () => {} });
        this.api.listTaxCategories().subscribe({ next: rows => this.taxCategories.set(rows), error: () => {} });
        this.api.listTaxRates().subscribe({ next: rows => this.taxRates.set(rows), error: () => {} });
        this.api.listTaxRules().subscribe({ next: rows => this.taxRules.set(rows), error: () => {} });
        this.api.listJurisdictionPacks().subscribe({ next: rows => this.jurisdictionPacks.set(rows), error: () => {} });
        this.api.listShippingZones().subscribe({ next: rows => this.shippingZones.set(rows), error: () => {} });
        this.loading.set(false);
      },
      error: err => {
        this.error.set(err?.message ?? 'load_failed');
        this.loading.set(false);
      }
    });
  }

  saveFxRate(): void {
    this.api.upsertFxRate({
      effectiveDateUtc: new Date(this.fxForm.effectiveDateUtc).toISOString(),
      fromCurrencyCode: this.fxForm.fromCurrencyCode,
      toCurrencyCode: this.fxForm.toCurrencyCode,
      rate: Number(this.fxForm.rate)
    }).subscribe({ next: () => this.reload() });
  }

  saveTaxJurisdiction(): void {
    this.api.upsertTaxJurisdiction({ ...this.taxJurisdictionForm }).subscribe({ next: () => this.reload() });
  }

  saveTaxCategory(): void {
    this.api.upsertTaxCategory({ ...this.taxCategoryForm }).subscribe({ next: () => this.reload() });
  }

  saveJurisdictionPack(): void {
    this.api.upsertJurisdictionPack({
      ...this.jurisdictionPackForm,
      regionCode: null,
      allowedCurrenciesJson: '[]',
      allowedPaymentMethodsJson: '[]',
      restrictedCategoryCodesJson: '[]',
      requiredInvoiceFieldsJson: '[]'
    }).subscribe({ next: () => this.reload() });
  }

  saveShippingZone(): void {
    this.api.upsertShippingZone({ ...this.shippingZoneForm, regionCode: null }).subscribe({ next: () => this.reload() });
  }

  checkEinvoice(): void {
    this.api.checkEInvoiceRequirements(this.einvoiceJurisdictionCode.trim().toUpperCase()).subscribe({
      next: result => this.einvoiceResult.set(result)
    });
  }

  decisionSeverity(decision: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (decision === 'Go') return 'success';
    if (decision === 'Conditional Go') return 'warn';
    if (decision === 'No Go') return 'danger';
    return 'secondary';
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ready') return 'success';
    if (status === 'conditional') return 'warn';
    if (status === 'blocker') return 'danger';
    return 'secondary';
  }
}
